import { onScopeDispose, watch } from 'vue'
import { RoomEvent } from 'livekit-client'

import type { RemoteParticipant } from 'livekit-client'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { refRoom } from '@hooks/classroom/room-live-kit.hook'

import dayjs from 'dayjs'

const CHAT_TOPIC = 'chat'

export interface ChatMessage {
  name: string
  time: string
  content: string
  isLocal: boolean
}

interface ChatPayload {
  message: string
  senderName: string
}

export interface RoomLiveKitChatOptions {
  getSenderName: () => string
  onMessage: (m: ChatMessage) => void
  onSent?: () => void
}

interface RoomLiveKitChatReturn {
  sendMessage: (text: string) => Promise<void>
}

const now = (): string => dayjs().format(DateFormat.AmPm)

let onIncoming: ((m: ChatMessage) => void) | null = null

const decodePayload = (bytes: Uint8Array): ChatPayload | null => {
  try {
    const parsed = JSON.parse(new TextDecoder().decode(bytes)) as Partial<ChatPayload>
    if (!parsed.message || !parsed.senderName) return null

    return { message: parsed.message, senderName: parsed.senderName }
  } catch {
    return null
  }
}

const handleData = (payload: Uint8Array, _participant?: RemoteParticipant, _kind?: unknown, topic?: string): void => {
  if (topic !== CHAT_TOPIC) return

  const data = decodePayload(payload)
  if (!data) return

  onIncoming?.({ name: data.senderName, time: now(), content: data.message, isLocal: false })
}

// Bound once to each new Room; independent of any consumer.
watch(refRoom, (room) => {
  room?.on(RoomEvent.DataReceived, handleData)
}, { immediate: true })

export const useRoomLiveKitChatReceiver = (onMessage: (m: ChatMessage) => void): void => {
  onIncoming = onMessage
  onScopeDispose(() => {
    if (onIncoming === onMessage) onIncoming = null
  })
}

export const useRoomLiveKitChat = (options: RoomLiveKitChatOptions): RoomLiveKitChatReturn => {
  const sendMessage = async (text: string): Promise<void> => {
    const trimmed = text.trim()
    const room = refRoom.value
    if (!trimmed || !room) return

    const senderName = options.getSenderName()
    const payload: ChatPayload = { message: trimmed, senderName }
    await room.localParticipant.publishData(
      new TextEncoder().encode(JSON.stringify(payload)) as Uint8Array<ArrayBuffer>,
      { reliable: true, topic: CHAT_TOPIC },
    )

    options.onMessage({ name: senderName, time: now(), content: trimmed, isLocal: true })
    options.onSent?.()
  }

  return { sendMessage }
}
